// A stat block's layout (issue 1575, decisions 2 and 3): which stats sit under which heading, in what order, and which
// headings start open. PRESENTATION, never rules: a game system declares one as data (`features.stat_block` in its
// `db/mrcs/<system>/system.json`, served by the site's registry as `stat_block`, outside `rules`, so no homebrew layer
// or ruling ever reaches it), and a DM may regroup it for their own screen, saved with their layout settings under the
// table's base-system label (`LayoutDoc.panel.statBlock`). Either way it only arranges rows the DM was already sent:
// a stat the layout names that the entry does not carry is simply not shown, and a row the layout does not place goes
// into "Other" — never dropped.
//
// The rows are an entry's stat block as the server reads it (`server/src/entries.ts` `detailFrom`): label, value and
// the attribute's `key`, which is what a layout names.
export const STAT_BLOCK_LIMITS = { sections: 16, stats: 120, label: 40 };
/** A heading's key, and a stat's: an attribute key as the site's authoring allows one, homebrew's `<layer>:` prefix too. */
const KEY_RE = /^(?:[a-z][a-z0-9-]{0,62}:)?[a-z0-9][a-z0-9._-]{0,99}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** The layout of a system that declares none: no headings, so every row is "Statistics", open. */
export const NO_STAT_BLOCK_LAYOUT = { sections: [] };
/** The catch-all heading's key: the rows no heading places. */
export const OTHER_SECTION = 'other';
/**
 * A layout as authored or stored, strictly: every heading a unique key, a label of 1–40 printable characters, `open`
 * a boolean (absent is closed), `stats` unique keys; a stat in two headings, or anything else outside the shape, reads
 * as null and is refused whole — so what a browser stores, every browser can read. `other` is not a heading's key.
 */
export function parseStatBlockLayout(v) {
    if (!isObj(v) || !Array.isArray(v.sections) || v.sections.length > STAT_BLOCK_LIMITS.sections)
        return null;
    const keys = new Set();
    const placed = new Set();
    const sections = [];
    for (const s of v.sections) {
        if (!isObj(s) || typeof s.key !== 'string' || !KEY_RE.test(s.key) || s.key === OTHER_SECTION || keys.has(s.key))
            return null;
        const label = typeof s.label === 'string' ? s.label.trim() : '';
        if (!label || label.length > STAT_BLOCK_LIMITS.label || /[\u0000-\u001f\u007f]/.test(label))
            return null;
        if (s.open !== undefined && typeof s.open !== 'boolean')
            return null;
        if (!Array.isArray(s.stats) || s.stats.length > STAT_BLOCK_LIMITS.stats)
            return null;
        const stats = [];
        for (const k of s.stats) {
            if (typeof k !== 'string' || !KEY_RE.test(k) || placed.has(k))
                return null;
            placed.add(k);
            stats.push(k);
        }
        if (placed.size > STAT_BLOCK_LIMITS.stats)
            return null;
        keys.add(s.key);
        sections.push({ key: s.key, label, open: s.open === true, stats });
    }
    return { sections };
}
/** A copy, so a layout handed out is never shared with the one kept. */
export const copyStatBlockLayout = (l) => ({ sections: l.sections.map((s) => ({ ...s, stats: [...s.stats] })) });
/**
 * A stat block's rows under a layout's headings: each heading's rows in the order the layout names them, a heading with
 * none left out, and every row no heading places under "Other" (closed), after them, in the stat block's own order —
 * or, with a layout of no headings (a system that declares none), under "Statistics", open.
 */
export function groupStats(rows, layout) {
    const byKey = new Map();
    for (const r of rows)
        if (r.key && !byKey.has(r.key))
            byKey.set(r.key, r);
    const used = new Set();
    const out = [];
    for (const s of layout.sections) {
        const mine = s.stats.map((k) => byKey.get(k)).filter((r) => !!r);
        for (const r of mine)
            used.add(r);
        if (mine.length)
            out.push({ key: s.key, label: s.label, open: s.open, rows: mine });
    }
    const rest = rows.filter((r) => !used.has(r));
    if (rest.length)
        out.push(layout.sections.length ? { key: OTHER_SECTION, label: 'Other', open: false, rows: rest } : { key: OTHER_SECTION, label: 'Statistics', open: true, rows: rest });
    return out;
}
/** A closed heading's one line (issue 1575): its first values, short — "30 ft. · Morningstar +2 (1d6)". */
export function groupSummary(g, max = 2) {
    const cut = (s) => (s.length > 22 ? `${s.slice(0, 21).trimEnd()}…` : s);
    const first = g.rows.slice(0, max).map((r) => cut(r.value.replace(/\s+/g, ' ').trim()));
    const more = g.rows.length - first.length;
    return [...first, ...(more > 0 ? [`+${more}`] : [])].join(' · ');
}
// ── A DM's regrouping (decision 3): moves and edits on a layout, each returning a new one ─────────────────────────────
/** A stat moved to a heading (`OTHER_SECTION`: out of every heading), at `index` among its stats (the end by default). */
export function moveStat(l, stat, to, index) {
    const sections = l.sections.map((s) => ({ ...s, stats: s.stats.filter((k) => k !== stat) }));
    const target = sections.find((s) => s.key === to);
    if (target) {
        const at = index === undefined ? target.stats.length : Math.max(0, Math.min(index, target.stats.length));
        target.stats.splice(at, 0, stat);
    }
    return { sections };
}
/** A heading moved up (-1) or down (+1). */
export function moveSection(l, key, by) {
    const i = l.sections.findIndex((s) => s.key === key);
    const j = i + by;
    if (i < 0 || j < 0 || j >= l.sections.length)
        return l;
    const sections = l.sections.map((s) => ({ ...s, stats: [...s.stats] }));
    [sections[i], sections[j]] = [sections[j], sections[i]];
    return { sections };
}
/** A heading renamed, or its "starts open" set. Null when the label would not be one a layout may hold. */
export function editSection(l, key, change) {
    const next = { sections: l.sections.map((s) => (s.key === key ? { ...s, stats: [...s.stats], ...(change.label !== undefined ? { label: change.label.trim() } : {}), ...(change.open !== undefined ? { open: change.open } : {}) } : { ...s, stats: [...s.stats] })) };
    return parseStatBlockLayout(next);
}
/** A new, empty heading at the end, named `label`; its key made from the label, unique. Null when it cannot be one. */
export function addSection(l, label) {
    const base = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'section';
    let key = /^[a-z0-9]/.test(base) && base !== OTHER_SECTION ? base : `s-${base}`;
    for (let n = 2; l.sections.some((s) => s.key === key); n++)
        key = `${base}-${n}`;
    return parseStatBlockLayout({ sections: [...l.sections, { key, label, open: false, stats: [] }] });
}
/** A heading taken away: its stats go to "Other". */
export function removeSection(l, key) {
    return { sections: l.sections.filter((s) => s.key !== key).map((s) => ({ ...s, stats: [...s.stats] })) };
}
//# sourceMappingURL=statBlock.js.map